import ValuationService from '@/services/ValuationService'
import { ValuationStepName } from '@/interfaces/valuation/ValuationStep'
import ValuationData, {
  ValuationStepData,
} from '@/interfaces/valuation/ValuationData'
import { PropertyType } from '@/interfaces/valuation/types/PropertyValuationStep'
import client from '@/services/api/client'
import createMessage from '@/services/app/createMessage'
import { MessageType } from '@/interfaces/app/Message'
import router from '@/router'
import {
  BuildingStatus,
  BuildingType,
} from '@/interfaces/valuation/types/BuildingValuationStep'
import { getNestedValue } from '@/util/helpers'
import { MutationTree, ActionTree, GetterTree, Module } from 'vuex'
import { ValuationProcessState } from '@/types/store/valuation'
import { RootState } from '@/types/store'

const mutations: MutationTree<ValuationProcessState> = {
  setValuationData(state, data: ValuationData): void {
    ValuationService.setDataToCache(data)
    state.valuationData = data
  },
  setCurrentStepData(state, data: ValuationStepData): void {
    state.valuationData.currentStep.data = data
  },
}

const actions: ActionTree<ValuationProcessState, RootState> = {
  goToStep({ commit, state }, stepName: ValuationStepName) {
    const data = ValuationService.goToStep(stepName, state.valuationData)
    commit('setValuationData', data)
  },
  resetValuationData({ commit, state }) {
    const data = ValuationService.resetData(state.valuationData)
    commit('setValuationData', data)
  },
  submitValuation({ commit, state, dispatch }) {
    commit('app/setLoading', true, { root: true })

    const locationType = getNestedValue(
      state.valuationData.steps[2],
      ['data', 'locationType'],
      ''
    )
    const location = getNestedValue(
      state.valuationData.steps[2],
      ['data', 'location'],
      {}
    )
    let propertyPrice = getNestedValue(
      state.valuationData.steps[3],
      ['data', 'propertyPrice'],
      0
    )
    propertyPrice = Number(
      propertyPrice
        .toString()
        .match(/(\d+)/g)
        .join('')
    )

    const unit = getNestedValue(
      state.valuationData.steps[3],
      ['data', 'unit'],
      {}
    )

    if (unit.apartmentNumber) {
      unit.apartmentNumber = Number(unit.apartmentNumber)
    }

    const data = {
      ...state.valuationData.steps[0].data,
      ...state.valuationData.steps[1].data,
      locationType,
      ...location,
      propertyPrice,
      ...unit,
      parentValuationId: state.valuationData.parentValuationId,
    }

    return client({
      url: `/v1/valuation`,
      method: 'POST',
      data: JSON.stringify(data),
    })
      .then(response => {
        const valuationNumber = getNestedValue(
          response,
          ['data', 'valuationNumber'],
          false
        )
        if (valuationNumber) {
          ValuationService.removeDataFromCache()
          const valuationData = ValuationService.resetData(state.valuationData)
          commit('setValuationData', valuationData)
          router.push({
            name: 'report',
            params: { id: valuationNumber },
          })
        }
      })
      .catch(err => {
        const errorCode = getNestedValue(err, ['data', 'errorCode'], 0)

        let message = createMessage(
          'Valuaci se nepodařilo spočítat. Zkuste to prosím později.',
          MessageType.Error
        )
        switch (errorCode) {
          case 1:
            message = createMessage(
              'V současné době nelze získat transakce z cenovamapa.org. Zkuste to prosím později.',
              MessageType.Error
            )
            break

          case 2:
            message = createMessage(
              'Nelze se připojit k databázi Valuátoru. Zkuste to prosím později.',
              MessageType.Error
            )
            break

          case 3:
            message = createMessage(
              'Nenalezen dostatek transakcí pro výpočet valuace. Upravte prosím zadání.',
              MessageType.Error
            )
            break

          default:
            break
        }

        dispatch(
          'app/addTempMessage',
          { message, timeout: 3500 },
          { root: true }
        )
      })
      .finally(() => commit('app/setLoading', false, { root: true }))
  },
  submitConcept({ commit, state, dispatch }) {
    commit('app/setLoading', true, { root: true })

    const locationType = getNestedValue(
      state.valuationData.steps[2],
      ['data', 'locationType'],
      ''
    )
    const location = getNestedValue(
      state.valuationData.steps[2],
      ['data', 'location'],
      {}
    )
    let propertyPrice = getNestedValue(
      state.valuationData.steps[3],
      ['data', 'propertyPrice'],
      0
    )
    propertyPrice = Number(
      propertyPrice
        .toString()
        .match(/(\d+)/g)
        .join('')
    )

    const unit = getNestedValue(
      state.valuationData.steps[3],
      ['data', 'unit'],
      {}
    )

    if (unit.apartmentNumber) {
      unit.apartmentNumber = Number(unit.apartmentNumber)
    }

    const data = {
      ...state.valuationData.steps[0].data,
      ...state.valuationData.steps[1].data,
      locationType,
      ...location,
      propertyPrice,
      ...unit,
      parentValuationId: state.valuationData.parentValuationId,
    }

    return client({
      url: `/v1/concept`,
      method: 'POST',
      data: JSON.stringify(data),
    })
      .then(() => {
        ValuationService.removeDataFromCache()
        const resetData = ValuationService.resetData(state.valuationData)
        commit('setValuationData', resetData)
        router.push({
          name: 'history',
        })
        const message = createMessage(
          'Koncept byl uložen. Najít ho můžete v historii.',
          MessageType.Info
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3500 },
          { root: true }
        )
      })
      .catch(err => {
        const message = createMessage(
          'Koncept se nepodařilo uložit. Zkuste to prosím později.',
          MessageType.Error
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3500 },
          { root: true }
        )
      })
      .finally(() => commit('app/setLoading', false, { root: true }))
  },
  async recreateValuation({ commit, dispatch }, valuationLocalId: number) {
    commit('app/setLoading', true, { root: true })
    return client({
      url: `/v1/valuation/${valuationLocalId}`,
      method: 'GET',
    })
      .then(response => {
        const valuationData = ValuationService.recreateValuationData(
          response.data
        )
        valuationData.parentValuationId = getNestedValue(
          response.data,
          ['valuationId'],
          null
        )
        commit('setValuationData', valuationData)
        if (router.currentRoute.name !== 'valuation') {
          router.push({
            name: 'valuation',
          })
        }
      })
      .catch(err => {
        const message = createMessage(
          'Nepodařilo se najít parametry valuace. Zkuste to prosím později.',
          MessageType.Error
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3000 },
          { root: true }
        )
      })
      .finally(() => commit('app/setLoading', false, { root: true }))
  },
  async recreateValuationFromValuationID(
    { commit, dispatch },
    valuationLocalId: number
  ) {
    commit('app/setLoading', true, { root: true })
    return client({
      url: `/v1/valuation/valuationID/${valuationLocalId}`,
      method: 'GET',
    })
      .then(response => {
        const valuationData = ValuationService.recreateValuationData(
          response.data
        )
        valuationData.parentValuationId = getNestedValue(
          response.data,
          ['valuationId'],
          null
        )
        commit('setValuationData', valuationData)
        const message = createMessage(
          'Parametry valuace načteny.',
          MessageType.Success
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3000 },
          { root: true }
        )
        if (router.currentRoute.name !== 'valuation') {
          router.push({
            name: 'valuation',
          })
        }
      })
      .catch(err => {
        const message = createMessage(
          'Nepodařilo se nalézt valuaci nebo k její duplikaci nemáte dostatečná práva.',
          MessageType.Error
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3000 },
          { root: true }
        )
      })
      .finally(() => commit('app/setLoading', false, { root: true }))
  },
  async recreateValuationFromConcept(
    { commit, dispatch },
    valuationLocalId: number
  ) {
    commit('app/setLoading', true, { root: true })
    return client({
      url: `/v1/concept/${valuationLocalId}`,
      method: 'GET',
    })
      .then(response => {
        const valuationData = ValuationService.recreateValuationData(
          response.data
        )
        valuationData.parentValuationId = getNestedValue(
          response.data,
          ['parentValuationId'],
          null
        )
        commit('setValuationData', valuationData)
        if (router.currentRoute.name !== 'valuation') {
          router.push({
            name: 'valuation',
          })
        }
      })
      .catch(err => {
        const message = createMessage(
          'Nepodařilo se najít parametry valuace. Zkuste to prosím později.',
          MessageType.Error
        )
        dispatch(
          'app/addTempMessage',
          { message, timeout: 3000 },
          { root: true }
        )
      })
      .finally(() => commit('app/setLoading', false, { root: true }))
  },
}

const getters: GetterTree<ValuationProcessState, RootState> = {
  valuationData: (state): ValuationData => state.valuationData,
  currentStepData: (state): ValuationStepData =>
    state.valuationData.currentStep.data,
  propertyType: (state): PropertyType =>
    getNestedValue(state.valuationData.steps[0], ['data', 'propertyType']),
  buildingStatus: (state): BuildingStatus =>
    getNestedValue(
      state.valuationData.steps[1],
      ['data', 'buildingStatus'],
      BuildingStatus.Finished
    ),
  buildingType: (state): BuildingType =>
    getNestedValue(
      state.valuationData.steps[1],
      ['data', 'buildingType'],
      BuildingType.New
    ),
}

export const valuationProcess: Module<ValuationProcessState, RootState> = {
  namespaced: true,
  state: {
    valuationData: ValuationService.getValuationData(),
  },
  mutations,
  actions,
  getters,
}

export default valuationProcess
